"use client";

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as React from "react";

import { cn } from "../lib/utils";
import { Icon } from "./icon";
import { Label, LabelProps } from "./label";

interface RadioGroupProps extends React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Root
> {
  classes?: {
    wrapper?: string;
  };
}

/**
 * Radix UI (API reference): https://www.radix-ui.com/docs/primitives/components/radio-group#api-reference
 *
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/radio-group
 */
const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ children, className, classes = {}, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn("grid gap-3", className, classes.wrapper)}
      {...props}
      ref={ref}
    >
      {children}
    </RadioGroupPrimitive.Root>
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

interface RadioGroupItemProps
  extends
    React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    Pick<LabelProps, "tooltipContent" | "tooltipIcon"> {
  label?: React.ReactNode;
}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, label, tooltipContent, tooltipIcon, ...props }, ref) => {
  const radioElement = (
    <RadioGroupPrimitive.Item
      className={cn(
        "aspect-square h-4 w-4 rounded-full border border-primary text-primary shadow focus:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      ref={ref}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Icon className="!size-3 text-current" icon="circle" weight="fill" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );

  const labelElement =
    typeof label === "string" || typeof label === "number" ? (
      <Label
        htmlFor={props.id}
        tooltipContent={tooltipContent}
        tooltipIcon={tooltipIcon}
      >
        {label}
      </Label>
    ) : (
      label
    );

  return (
    <div className="flex items-center gap-2">
      {radioElement}
      {labelElement}
    </div>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export {
  RadioGroup,
  RadioGroupItem,
  type RadioGroupItemProps,
  type RadioGroupProps,
};
